/**
 * FaxReportModal — pick which of a patient's outbound faxes go on a printable
 * delivery report, then open it (PDF, inline → the browser's print dialog) or
 * download it.
 *
 * Reads GET /contacts/:id/faxes (every outbound transmission, newest first,
 * with the recipient office + delivery moment resolved server-side) and POSTs
 * the chosen ids to /contacts/:id/fax-report, which answers with the PDF.
 * Delivered faxes are pre-selected — that is the report people actually need
 * ("prove it got there"); failed/in-flight rows can be ticked on to document
 * the attempts.
 */
import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Printer } from 'lucide-react';
import api from '../../../services/api';
import { Contact, ContactFax } from '../../types';
import { triggerBlobDownload } from '../../lib';
import { Alert, Z, btnGhost, btnPrimary } from '../ui';

interface Props {
  contact: Contact;
  onClose: () => void;
}

const errMsg = async (e: any, fallback: string): Promise<string> => {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try {
      const p = JSON.parse(await d.text());
      return p.detail ? `${p.error} — ${p.detail}` : (p.error || fallback);
    } catch { return fallback; }
  }
  if (d?.error) return d.detail ? `${d.error} — ${d.detail}` : d.error;
  return e?.message || fallback;
};

const STATUS_PILL: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  sending: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  queued: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function filenameFromDisposition(disposition?: string, fallback = 'fax-report.pdf'): string {
  const m = /filename="?([^"]+)"?/.exec(disposition || '');
  return m ? m[1] : fallback;
}

export default function FaxReportModal({ contact, onClose }: Props) {
  const [faxes, setFaxes] = useState<ContactFax[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'print' | 'download' | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/api/crm/contacts/${contact.id}/faxes`)
      .then((res) => {
        if (!alive) return;
        const rows: ContactFax[] = res.data?.faxes || [];
        setFaxes(rows);
        setSelected(new Set(rows.filter((f) => f.status === 'delivered').map((f) => f.id)));
        setError(null);
      })
      .catch(async (e) => { if (alive) setError(await errMsg(e, 'Failed to load faxes')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [contact.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const deliveredIds = useMemo(() => faxes.filter((f) => f.status === 'delivered').map((f) => f.id), [faxes]);
  const allSelected = faxes.length > 0 && selected.size === faxes.length;

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const build = async (mode: 'print' | 'download') => {
    if (busy || selected.size === 0) return;
    setBusy(mode);
    setError(null);
    // Open the tab synchronously (inside the click) so popup blockers allow it;
    // the PDF is loaded into it once the request returns.
    const tab = mode === 'print' ? window.open('', '_blank') : null;
    try {
      const res = await api.post(
        `/api/crm/contacts/${contact.id}/fax-report`,
        { fax_ids: Array.from(selected) },
        { responseType: 'blob' },
      );
      const blob: Blob = res.data;
      const filename = filenameFromDisposition(res.headers?.['content-disposition']);
      if (mode === 'download') {
        triggerBlobDownload(blob, filename);
        return;
      }
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup blocked: fall back to a download so the user still gets it.
        triggerBlobDownload(blob, filename);
      }
      // Revoke after the tab has had time to load the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      if (tab) tab.close();
      setError(await errMsg(e, 'Failed to build the report'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${Z.modal} flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4`}
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-t-xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Fax delivery report"
      >
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Fax delivery report</h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Choose the transmissions to include. Delivered faxes are pre-selected.
          </p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {error && <Alert tone="error" size="sm" icon={false} className="mb-3">{error}</Alert>}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : faxes.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No faxes have been sent for this patient yet.
            </p>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span className="text-gray-500 dark:text-gray-400">{selected.size} of {faxes.length} selected</span>
                <button
                  type="button"
                  onClick={() => setSelected(allSelected ? new Set() : new Set(faxes.map((f) => f.id)))}
                  className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
                {deliveredIds.length > 0 && deliveredIds.length < faxes.length && (
                  <button
                    type="button"
                    onClick={() => setSelected(new Set(deliveredIds))}
                    className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Delivered only
                  </button>
                )}
              </div>

              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {faxes.map((f) => {
                  const checked = selected.has(f.id);
                  const pill = STATUS_PILL[f.status] || STATUS_PILL.queued;
                  return (
                    <li key={f.id}>
                      <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(f.id)}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                              {f.recipient_name || f.to_number_display || 'Unknown recipient'}
                            </span>
                            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${pill}`}>
                              {f.status_label}
                            </span>
                          </div>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                            {[f.recipient_location, f.to_number_display].filter(Boolean).join(' · ')}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Sent {fmtWhen(f.created_at)}
                            {f.status === 'delivered' && f.delivered_at && (
                              <span className="text-green-700 dark:text-green-400"> · Delivered {fmtWhen(f.delivered_at)}</span>
                            )}
                            {f.status === 'failed' && (
                              <span className="text-red-700 dark:text-red-400"> · Failed{f.error ? ` (${f.error})` : ''}</span>
                            )}
                            {f.attempt > 1 && <span> · attempt {f.attempt}</span>}
                            {f.page_count ? <span> · {f.page_count} pages</span> : null}
                          </p>
                          <p className="truncate text-[11px] text-gray-400 dark:text-gray-500">
                            {[f.document_filename, f.reference_code, f.sent_by ? `by ${f.sent_by}` : null].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <button type="button" onClick={onClose} className={btnGhost}>Close</button>
          <button
            type="button"
            onClick={() => build('download')}
            disabled={!!busy || selected.size === 0}
            className={btnGhost}
            title="Save the report as a PDF"
          >
            {busy === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download PDF
          </button>
          <button
            type="button"
            onClick={() => build('print')}
            disabled={!!busy || selected.size === 0}
            className={btnPrimary}
            title="Open the report in a new tab to print"
          >
            {busy === 'print' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Open &amp; print
          </button>
        </div>
      </div>
    </div>
  );
}
